/**
 * Derives order-level UI permissions (button visibility, review button display)
 * based on the current user's role, assignment, and facility configuration.
 */
import {
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { RootState } from "../../../redux/store/store";
import { shouldShowReviewButton } from "../../worklists/myPaOrders/utils/reviewHelper";

/**
 * Computes order-level permission flags for the single-order layout using
 * the Postgres-backed data-accumulator context.
 *
 * - `hideButton` — hides action buttons for non-admin users who are not
 *   the assignee on the order. Admins and super admins always see the
 *   buttons. For non-admins, the user must match `assigned_to_id` on the
 *   order; when both the order's `org_id` and the user's `FacilityId` are
 *   present, they must also match. A missing facility on either side is
 *   treated as "do not disqualify" so legitimate assignees aren't hidden
 *   when the order has no facility recorded.
 * - `shouldShowReviewButtonValue` — gates the review button based on
 *   facility config, order status, and current user assignment.
 *
 * @returns `hideButton` and `shouldShowReviewButtonValue` flags
 */
export const useOrderPermissions = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const hideButton = useMemo(() => {
    if (isCurrentUserAdmin() || isCurrentUserSuperAdmin()) return false;
    const assignedId = generalOrderDetails?.order?.assigned_to?.assigned_to_id;
    const assignedFacility = generalOrderDetails?.order?.org_id;
    const currentUserId = providerDetails?.id;
    const currentUserFacility = providerDetails?.FacilityId;

    // Guard against `undefined === undefined` accidentally matching while
    // provider details or assignment metadata are still loading.
    if (!currentUserId || !assignedId) return true;
    if (assignedId !== currentUserId) return true;

    // Only hide on facility mismatch when both sides have a facility set;
    // missing values on either side are treated as non-disqualifying.
    if (
      assignedFacility &&
      currentUserFacility &&
      assignedFacility !== currentUserFacility
    ) {
      return true;
    }

    return false;
  }, [providerDetails, generalOrderDetails?.order]);

  const shouldShowReviewButtonValue = useMemo(() => {
    return shouldShowReviewButton(
      singleHealthFacilityDetails,
      generalOrderDetails?.order?.status?.medical_order_status,
      generalOrderDetails?.order?.status?.medical_order_review_status,
      generalOrderDetails?.order?.status?.master_auth_status,
      generalOrderDetails?.order?.assigned_to?.assigned_to_id,
      user?.id,
    );
  }, [
    singleHealthFacilityDetails,
    generalOrderDetails?.order?.status?.medical_order_status,
    generalOrderDetails?.order?.status?.medical_order_review_status,
    generalOrderDetails?.order?.status?.master_auth_status,
    generalOrderDetails?.order?.assigned_to?.assigned_to_id,
    user?.id,
  ]);

  return {
    hideButton,
    shouldShowReviewButtonValue,
  };
};
